import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '../LocaleLink';
import { stripLangPrefix } from '../../utils/languages';
import { ChevronDown, Moon, Sun, Globe } from 'lucide-react';
import { TOP_NAV, isGroupHeading, type NavDropdownEntry } from '../../data/topNav';
import { useDarkMode } from '../../utils/useDarkMode';
import LanguageModal, { useCurrentLangMeta } from './LanguageModal';
import LogoLockup from '../brand/LogoLockup';
import { useLang } from '../../utils/i18n';
import { localizedRouteLabel, navGroupLabel, topNavLabel } from '../../utils/chromeLabels';

/**
 * Desktop navigation bar (≥1024 px).
 *
 * Wheel | Templates | Education | Giveaways | Resources | About, a primary
 * "Spin the Wheel →" CTA, and icon-only utilities. Items with a dropdown open
 * a menu anchored under their trigger; the menu is absolutely positioned so it
 * overlays page content and never pushes the wheel downward (no CLS).
 */
const MegaNav: React.FC = () => {
  const [openId, setOpenId]     = useState<string | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const { pathname }            = useLocation();
  const barePath                = stripLangPrefix(pathname);
  const { isDark, toggle: toggleDark } = useDarkMode();
  const currentLang             = useCurrentLangMeta();
  const { lang, t }             = useLang();

  const navRef = useRef<HTMLElement>(null);

  // ── Close on click-outside ──
  useEffect(() => {
    if (!openId) return;
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openId]);

  // ── Escape: close menu and return focus to its trigger ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpenId(prev => {
        if (prev) document.getElementById(`${prev}-trigger`)?.focus();
        return null;
      });
      setLangOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Close on route change ──
  useEffect(() => { setOpenId(null); }, [pathname]);

  const toggle = useCallback((id: string) => setOpenId(prev => (prev === id ? null : id)), []);

  // Move focus into the menu when it opens via keyboard (ArrowDown).
  const focusFirstItem = (id: string) => {
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(`#${id}-menu [role="menuitem"]`)?.focus();
    });
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown'
      ? items[(idx + 1) % items.length]
      : items[(idx - 1 + items.length) % items.length];
    next?.focus();
  };

  const isEntryActive = (e: NavDropdownEntry) => !isGroupHeading(e) && barePath === e.to.split('?')[0];

  const linkBase =
    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide whitespace-nowrap transition-all duration-150';
  const linkIdle   = 'text-gray-300 hover:text-white hover:bg-gray-800';
  const linkActive = 'bg-violet-600 text-white shadow shadow-violet-900/50';

  return (
    <>
      <header className="bg-gray-950 border-b border-gray-800/80 sticky top-0 z-30 shadow-xl">
        <div className="w-full px-3 sm:px-5">
          <div className="flex items-center h-12 gap-2">

            {/* Logo */}
            <Link
              to="/"
              className="flex items-center flex-shrink-0 me-3"
              aria-label={`TheNameWheel — ${t('nav.home')}`}
            >
              <LogoLockup />
            </Link>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Primary nav */}
            <nav ref={navRef} className="flex items-center gap-0.5" aria-label={t('chrome.mainNavigation')}>
              <ul className="flex items-center gap-0.5 list-none m-0 p-0">
                {TOP_NAV.map((item, index) => {
                  if (item.to) {
                    const active = barePath === item.to;
                    return (
                      <li key={item.id}>
                        <Link
                          to={item.to}
                          aria-current={active ? 'page' : undefined}
                          className={`${linkBase} ${active ? linkActive : linkIdle}`}
                        >
                          {topNavLabel(item.id, item.label, t)}
                        </Link>
                      </li>
                    );
                  }

                  const isOpen   = openId === item.id;
                  const isActive = !!item.dropdown?.some(isEntryActive);
                  // Right-hand items open leftwards so menus never overflow the viewport.
                  const alignEnd = index >= 3;

                  return (
                    <li key={item.id} className="relative">
                      <button
                        id={`${item.id}-trigger`}
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        aria-controls={`${item.id}-menu`}
                        aria-current={isActive && !isOpen ? 'true' : undefined}
                        onClick={() => toggle(item.id)}
                        onKeyDown={e => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (!isOpen) setOpenId(item.id);
                            focusFirstItem(item.id);
                          }
                        }}
                        className={`${linkBase} ${isActive || isOpen ? linkActive : linkIdle}`}
                      >
                        {topNavLabel(item.id, item.label, t)}
                        <ChevronDown
                          size={11}
                          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </button>

                      {isOpen && (
                        <ul
                          id={`${item.id}-menu`}
                          role="menu"
                          aria-labelledby={`${item.id}-trigger`}
                          onKeyDown={onMenuKeyDown}
                          className={`absolute ${alignEnd ? 'end-0' : 'start-0'} top-full mt-1.5 w-[21rem] max-h-[calc(100vh-4.5rem)] overflow-y-auto rounded-xl border border-gray-700 bg-gray-900 p-1.5 shadow-2xl list-none z-40`}
                        >
                          {item.dropdown?.map(entry => {
                            if (isGroupHeading(entry)) {
                              return (
                                <li key={`group-${entry.group}`} role="none" className="px-2 pt-2.5 pb-1">
                                  <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                                    {navGroupLabel(entry.group, t)}
                                  </p>
                                </li>
                              );
                            }
                            const active = isEntryActive(entry);
                            return (
                              <li key={entry.to} role="none">
                                <Link
                                  to={entry.to}
                                  role="menuitem"
                                  aria-current={active ? 'page' : undefined}
                                  onClick={() => setOpenId(null)}
                                  className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
                                    active
                                      ? 'bg-violet-600/20 text-violet-300'
                                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                  }`}
                                >
                                  {entry.emoji && (
                                    <span className="w-5 text-center flex-shrink-0 mt-0.5 text-base" aria-hidden="true">
                                      {entry.emoji}
                                    </span>
                                  )}
                                  <span className="min-w-0">
                                    <span className="block font-semibold leading-tight">{localizedRouteLabel(lang, entry.to, entry.label)}</span>
                                    {lang === 'en' && entry.blurb && (
                                      <span className="block text-xs text-gray-400 leading-snug mt-0.5">
                                        {entry.blurb}
                                      </span>
                                    )}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Primary CTA + icon-only utilities */}
            <div className="flex items-center gap-1">
              <a
                href="https://discord.gg/FKjedfPxTf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-1.5 ms-2 me-1 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-lg text-xs font-bold tracking-wide whitespace-nowrap transition-colors shadow-sm shadow-[#5865F2]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5865F2]"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                </svg>
                Discord
              </a>

              {/* Dark mode */}
              <button
                onClick={toggleDark}
                aria-label={isDark ? t('chrome.light') : t('chrome.dark')}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-all"
              >
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              {/* Language */}
              <button
                onClick={() => setLangOpen(true)}
                aria-label={t('chrome.language')}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-gray-300 hover:text-white hover:bg-gray-800 transition-all"
              >
                <Globe size={15} />
                <span className="uppercase">{currentLang.code}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Language modal */}
      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
    </>
  );
};

export default MegaNav;
